/**
 * RestoID — shared social sign-in completion (verify → identify → redirect).
 * ---------------------------------------------------------------------------
 * The completion "tail" mirrored from the upstream social callback
 * (src/pages/SocialSignInWebCallback/use-social-sign-in-listener.ts): given a
 * social verificationId + connectorData, verify it, identify, and on
 * `user.identity_not_exist` branch to register / link exactly as upstream does.
 *
 * Isolated here (rather than inlined in the widget hook) so this is the SINGLE
 * definition of "what happens after a social verify" — the part most likely to
 * drift from upstream when Logto changes social completion. Any in-page social
 * connector (today: the Yandex ID Suggest widget) calls `complete`; the standard
 * redirect button keeps using upstream's own listener untouched.
 */
import { type RequestErrorBody } from '@logto/schemas';
import { InteractionEvent, SignInMode, VerificationType, experience } from '@logto/schemas';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { validate } from 'superstruct';

import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { identifyAndSubmitInteraction, verifySocialVerification } from '@/apis/experience';
import useBindSocialRelatedUser from '@/containers/SocialLinkAccount/use-social-link-related-user';
import useApi from '@/hooks/use-api';
import useErrorHandler, { type ErrorHandlers } from '@/hooks/use-error-handler';
import useGlobalRedirectTo from '@/hooks/use-global-redirect-to';
import { useSieMethods } from '@/hooks/use-sie';
import useSocialRegister from '@/hooks/use-social-register';
import useSubmitInteractionErrorHandler from '@/hooks/use-submit-interaction-error-handler';
import useToast from '@/hooks/use-toast';
import { socialAccountNotExistErrorDataGuard } from '@/types/guard';

type SocialCompletionArgs = {
  /** The social verificationId issued by getSocialAuthorizationUrl. */
  verificationId: string;
  /** Connector payload to verify (redirectUri + the provider's OAuth result). */
  connectorData: Record<string, unknown>;
  /** Returns false once the caller is unmounted/cancelled — skips late state writes. */
  isActive?: () => boolean;
};

/**
 * Returns `complete` (run the verify→identify→redirect chain for an already-issued
 * social verification) and `backToSignIn` (toast + navigate to sign-in), for an
 * in-page social connector identified by `connectorId`.
 */
const useSocialCompletion = (connectorId: string) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signInMode, socialSignInSettings } = useSieMethods();
  const { setToast } = useToast();
  const { setVerificationId } = useContext(UserInteractionContext);

  const asyncVerifySocial = useApi(verifySocialVerification);
  const asyncIdentify = useApi(identifyAndSubmitInteraction);
  const handleError = useErrorHandler();
  const redirectTo = useGlobalRedirectTo();
  // Mirrors upstream's `navigateToSignIn` — the escape hatch used when a social
  // registration is rejected by the email blocklist.
  const navigateToSignIn = useCallback(() => {
    navigate('/' + experience.routes.signIn, { replace: true });
  }, [navigate]);

  const registerWithSocial = useSocialRegister(connectorId, {
    replace: true,
    onEmailBlocked: navigateToSignIn,
  });
  const bindSocialRelatedUser = useBindSocialRelatedUser();
  const preSignInErrorHandler = useSubmitInteractionErrorHandler(InteractionEvent.SignIn, {
    replace: true,
  });

  // Holds the verified id between verify and identify so the account-not-exist
  // handler can consume it (mirrors upstream's verificationId reassignment).
  const verificationIdRef = useRef<string>();

  const backToSignIn = useCallback(
    (message: string) => {
      setToast(message);
      navigate('/' + experience.routes.signIn);
    },
    [navigate, setToast]
  );

  // Mirror of the upstream callback's account-not-exist handling (register / link).
  const accountNotExistErrorHandler = useCallback(
    async (error: RequestErrorBody) => {
      const verificationId = verificationIdRef.current;
      // Defensive parity guard (mirrors upstream): the ref is always set just
      // before identify, so this never trips in the in-page flow — keep it anyway.
      if (!verificationId) {
        backToSignIn(t('error.invalid_session'));
        return;
      }
      const [, data] = validate(error.data, socialAccountNotExistErrorDataGuard);
      const { relatedUser } = data ?? {};

      if (relatedUser) {
        if (socialSignInSettings.automaticAccountLinking) {
          await bindSocialRelatedUser(verificationId);
        } else {
          navigate(`/social/link/${connectorId}`, { replace: true, state: { relatedUser } });
        }
        return;
      }

      if (signInMode === SignInMode.SignIn) {
        backToSignIn(error.message);
        return;
      }

      await registerWithSocial(verificationId);
    },
    [
      backToSignIn,
      bindSocialRelatedUser,
      connectorId,
      navigate,
      registerWithSocial,
      signInMode,
      socialSignInSettings.automaticAccountLinking,
      t,
    ]
  );

  const signInErrorHandlers = useMemo<ErrorHandlers>(
    () => ({
      'user.identity_not_exist': accountNotExistErrorHandler,
      ...preSignInErrorHandler,
      global: async (error) => backToSignIn(error.message),
    }),
    [accountNotExistErrorHandler, preSignInErrorHandler, backToSignIn]
  );

  const complete = useCallback(
    async ({ verificationId, connectorData, isActive }: SocialCompletionArgs) => {
      const active = isActive ?? (() => true);

      const [verifyError, verifyResult] = await asyncVerifySocial(connectorId, {
        verificationId,
        connectorData,
      });
      if (!active()) {
        return;
      }
      if (verifyError || !verifyResult) {
        await handleError(verifyError, { global: async (error) => backToSignIn(error.message) });
        return;
      }

      verificationIdRef.current = verifyResult.verificationId;
      setVerificationId(VerificationType.Social, verifyResult.verificationId);

      const [identifyError, identifyResult] = await asyncIdentify({
        verificationId: verifyResult.verificationId,
      });
      if (!active()) {
        return;
      }
      if (identifyError) {
        await handleError(identifyError, signInErrorHandlers);
        return;
      }
      if (identifyResult?.redirectTo) {
        await redirectTo(identifyResult.redirectTo);
      }
    },
    [
      asyncIdentify,
      asyncVerifySocial,
      backToSignIn,
      connectorId,
      handleError,
      redirectTo,
      setVerificationId,
      signInErrorHandlers,
    ]
  );

  return { complete, backToSignIn };
};

export default useSocialCompletion;
